import { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../services/apiClient';
import UploadDropzone from '../components/UploadDropzone';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const UPLOAD_PATH = '/api/admin/hero-panels/upload';
const COLOR_PRESETS = ['#f9d7df', '#e9dfd6', '#d9f2dd'];

const EMPTY_HEADER = {
  title: '',
  subtitle: '',
  isActive: true,
};

const EMPTY_FORM = {
  titleTop: '',
  titleMain: '',
  ctaText: '',
  ctaLink: '',
  bgColor: COLOR_PRESETS[0],
  imageUrl: '',
  order: 1,
  isActive: true,
};

const normalize = (item) => ({
  id: item?.id ?? item?._id,
  titleTop: item?.titleTop ?? item?.title_top ?? '',
  titleMain: item?.titleMain ?? item?.title_main ?? '',
  ctaText: item?.ctaText ?? item?.cta_text ?? '',
  ctaLink: item?.ctaLink ?? item?.cta_link ?? '',
  bgColor: item?.bgColor ?? item?.bg_color ?? COLOR_PRESETS[0],
  imageUrl: item?.imageUrl ?? item?.image_url ?? '',
  order: Number(item?.order ?? item?.sort_order ?? 1),
  isActive: item?.isActive ?? item?.is_active ?? true,
});

const normalizeHeader = (item) => ({
  title: item?.title ?? item?.headerTitle ?? '',
  subtitle: item?.subtitle ?? item?.headerSubtitle ?? '',
  isActive: item?.isActive ?? item?.is_active ?? true,
});

const nextOrderValue = (list) => {
  const maxOrder = list.reduce((max, item) => {
    const value = Number(item?.order);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  return Math.max(1, maxOrder + 1);
};

const isOrderTaken = (list, order, editingId = null) =>
  list.some((item) => item && item.id !== editingId && Number(item.order) === Number(order));

export default function ContentHeroPanelsPage({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerLoading, setHeaderLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [headerError, setHeaderError] = useState('');
  const [editing, setEditing] = useState(null);
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/hero-panels', token);
      const list = res?.items || res?.data || res || [];
      const normalized = Array.isArray(list) ? list.map(normalize) : [];
      setItems(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load hero panels');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHeader = async () => {
    setHeaderLoading(true);
    setHeaderError('');
    try {
      const res = await apiGet('/api/admin/hero-panels/header', token);
      setHeader(normalizeHeader(res));
    } catch (err) {
      setHeader(EMPTY_HEADER);
      setHeaderError(err.message || 'Failed to load header.');
    } finally {
      setHeaderLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editing) return;
    setForm((prev) => {
      const current = Number(prev.order);
      if (items.length === 0) {
        return current === 1 ? prev : { ...prev, order: 1 };
      }
      if (!Number.isFinite(current) || current < 1 || current === 1) {
        return { ...prev, order: nextOrderValue(items) };
      }
      if (isOrderTaken(items, current)) {
        return { ...prev, order: nextOrderValue(items) };
      }
      return prev;
    });
  }, [items, editing]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0) || String(a.id).localeCompare(String(b.id)));
  }, [items]);

  const validate = () => {
    if (!String(form.titleTop || '').trim()) return 'titleTop is required.';
    if (!String(form.titleMain || '').trim()) return 'titleMain is required.';
    if (!String(form.bgColor || '').trim()) return 'bgColor is required.';
    if (!String(form.imageUrl || '').trim()) return 'imageUrl is required.';
    const order = Number(form.order);
    if (!Number.isFinite(order) || order < 1) return 'order must be >= 1.';
    return '';
  };

  const payload = () => ({
    titleTop: String(form.titleTop || '').trim(),
    titleMain: String(form.titleMain || '').trim(),
    ctaText: String(form.ctaText || '').trim(),
    ctaLink: String(form.ctaLink || '').trim(),
    bgColor: String(form.bgColor || '').trim(),
    imageUrl: String(form.imageUrl || '').trim(),
    order: Math.max(1, Math.floor(Number(form.order) || 1)),
    isActive: Boolean(form.isActive),
  });

  const saveHeader = async () => {
    const title = String(header.title || '').trim();
    const subtitle = String(header.subtitle || '').trim();
    const isActive = Boolean(header.isActive);

    if (isActive && !title) {
      setHeaderError('Header title is required.');
      return;
    }

    setHeaderError('');
    setHeaderSaving(true);
    setError('');
    try {
      await apiPut(
        '/api/admin/hero-panels/header',
        { title, subtitle, isActive },
        token
      );
      await loadHeader();
    } catch (err) {
      setHeaderError(err.message || 'Header save failed');
    } finally {
      setHeaderSaving(false);
    }
  };

  const save = async () => {
    const validation = validate();
    if (validation) {
      setFormError(validation);
      return;
    }
    setFormError('');
    setSaving(true);
    setError('');
    try {
      const basePayload = payload();
      const resolvedOrder = editing
        ? basePayload.order
        : isOrderTaken(items, basePayload.order)
        ? nextOrderValue(items)
        : basePayload.order;
      const createPayload = { ...basePayload, order: resolvedOrder };

      if (editing?.id) {
        await apiPut(`/api/admin/hero-panels/${editing.id}`, basePayload, token);
      } else {
        await apiPost('/api/admin/hero-panels', createPayload, token);
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      const message = err.message || 'Save failed';
      if (!editing && /already exists/i.test(message)) {
        try {
          const basePayload = payload();
          const retryPayload = { ...basePayload, order: nextOrderValue(items) };
          await apiPost('/api/admin/hero-panels', retryPayload, token);
          setEditing(null);
          setForm(EMPTY_FORM);
          await load();
          setSaving(false);
          return;
        } catch (retryErr) {
          setError(retryErr.message || message);
        }
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setEditing(item);
    setForm({
      ...EMPTY_FORM,
      ...item,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this slide?')) return;
    setError('');
    try {
      await apiDelete(`/api/admin/hero-panels/${id}`, token);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const toggleActive = async (item) => {
    setError('');
    try {
      await apiPatch(`/api/admin/hero-panels/${item.id}/toggle`, { isActive: !item.isActive }, token);
      await load();
    } catch (err) {
      setError(err.message || 'Toggle failed');
    }
  };

  const swapOrder = async (fromIdx, toIdx) => {
    const list = sortedItems;
    const a = list[fromIdx];
    const b = list[toIdx];
    if (!a || !b) return;
    setError('');
    try {
      await apiPatch(
        '/api/admin/hero-panels/reorder',
        [
          { id: a.id, order: b.order },
          { id: b.id, order: a.order },
        ],
        token
      );
      await load();
    } catch (err) {
      setError(err.message || 'Reorder failed');
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Hero Panels Slider</h4>
          <small className="text-muted">Manage the Travelz-style 3 panel slider under the home hero banner.</small>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="rounded border p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">Section header</div>
          <Button size="sm" variant="outline-secondary" onClick={loadHeader} disabled={headerLoading || headerSaving}>
            {headerLoading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        <div className="text-muted small mb-2">Displayed above the 3-panel slider on the homepage.</div>

        {headerError ? <Alert variant="danger" className="py-2">{headerError}</Alert> : null}

        <Row className="g-2 align-items-end">
          <Col md={4}>
            <Form.Label className="small text-muted mb-1">Header title</Form.Label>
            <Form.Control
              value={header.title}
              onChange={(e) => setHeader((p) => ({ ...p, title: e.target.value }))}
              placeholder="Services"
            />
          </Col>
          <Col md={5}>
            <Form.Label className="small text-muted mb-1">Sub-header (optional)</Form.Label>
            <Form.Control
              value={header.subtitle}
              onChange={(e) => setHeader((p) => ({ ...p, subtitle: e.target.value }))}
              placeholder="Optional description"
            />
          </Col>
          <Col md={1} className="pt-4">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={!!header.isActive}
              onChange={(e) => setHeader((p) => ({ ...p, isActive: e.target.checked }))}
            />
          </Col>
          <Col md={2} className="text-end">
            <Button onClick={saveHeader} disabled={headerSaving}>
              {headerSaving ? 'Saving...' : 'Save header'}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="rounded border p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">{editing ? 'Edit slide' : 'Create new slide'}</div>
          {editing ? (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY_FORM);
                setFormError('');
              }}
              disabled={saving}
            >
              Cancel edit
            </Button>
          ) : null}
        </div>

        {formError && <Alert variant="danger">{formError}</Alert>}

        <Row className="g-2">
          <Col md={3}>
            <Form.Label className="small text-muted mb-1">Title top</Form.Label>
            <Form.Control
              value={form.titleTop}
              onChange={(e) => setForm((p) => ({ ...p, titleTop: e.target.value }))}
              placeholder="Book your"
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small text-muted mb-1">Title main</Form.Label>
            <Form.Control
              value={form.titleMain}
              onChange={(e) => setForm((p) => ({ ...p, titleMain: e.target.value }))}
              placeholder="Car Rentals"
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small text-muted mb-1">CTA text</Form.Label>
            <Form.Control
              value={form.ctaText}
              onChange={(e) => setForm((p) => ({ ...p, ctaText: e.target.value }))}
              placeholder="BOOK NOW"
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small text-muted mb-1">CTA link</Form.Label>
            <Form.Control
              value={form.ctaLink}
              onChange={(e) => setForm((p) => ({ ...p, ctaLink: e.target.value }))}
              placeholder="/stays or https://..."
            />
          </Col>
        </Row>

        <Row className="g-2 mt-2 align-items-end">
          <Col md={3}>
            <Form.Label className="small text-muted mb-1">Background color</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="color"
                value={form.bgColor}
                onChange={(e) => setForm((p) => ({ ...p, bgColor: e.target.value }))}
                style={{ width: 54, height: 42, padding: 2 }}
              />
              <Form.Control
                value={form.bgColor}
                onChange={(e) => setForm((p) => ({ ...p, bgColor: e.target.value }))}
                placeholder="#f9d7df"
              />
            </div>
            <div className="d-flex gap-2 mt-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, bgColor: c }))}
                  className="border rounded"
                  style={{ width: 28, height: 28, backgroundColor: c }}
                  aria-label={`Use ${c}`}
                />
              ))}
            </div>
          </Col>
          <Col md={3}>
            <Form.Label className="small text-muted mb-1">Order</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={form.order}
              onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
            />
          </Col>
          <Col md={3}>
            <Form.Check
              type="checkbox"
              className="mt-4"
              label="Active"
              checked={!!form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
          </Col>
          <Col md={3} className="text-end">
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update slide' : 'Create slide'}
            </Button>
          </Col>
        </Row>

        <Row className="g-2 mt-2 align-items-center">
          <Col md={6}>
            <Form.Label className="small text-muted mb-1">Image URL</Form.Label>
            <Form.Control
              value={form.imageUrl}
              onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))}
              placeholder="/uploads/hero-panels/... or https://..."
            />
          </Col>
          <Col md={3}>
            <UploadDropzone
              token={token}
              uploadPath={UPLOAD_PATH}
              label="Upload illustration"
              onUploaded={(path) => setForm((p) => ({ ...p, imageUrl: path }))}
              onUploadedMany={(paths) => setForm((p) => ({ ...p, imageUrl: paths[0] || p.imageUrl }))}
            />
          </Col>
          <Col md={3}>
            {form.imageUrl ? (
              <div className="border rounded" style={{ width: '100%', height: 110, overflow: 'hidden', background: '#f8fafc' }}>
                <img
                  src={resolveAssetUrl(form.imageUrl)}
                  alt={form.titleMain || 'Slide'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <small className="text-muted">No image selected</small>
            )}
          </Col>
        </Row>
      </div>

      {loading ? (
        <div className="py-4 text-center">
          <Spinner animation="border" />
        </div>
      ) : null}

      <Table hover responsive bordered size="sm">
        <thead className="table-light">
          <tr>
            <th>Preview</th>
            <th>Titles</th>
            <th>BG</th>
            <th>Order</th>
            <th>Status</th>
            <th>CTA</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, idx) => (
            <tr key={item.id || idx}>
              <td style={{ width: 140 }}>
                {item.imageUrl ? (
                  <div className="border rounded" style={{ width: 120, height: 70, overflow: 'hidden', background: item.bgColor }}>
                    <img
                      src={resolveAssetUrl(item.imageUrl)}
                      alt={item.titleMain || 'Slide'}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <span className="text-muted">No image</span>
                )}
              </td>
              <td>
                <div className="fw-semibold">{item.titleTop}</div>
                <div className="text-muted">{item.titleMain}</div>
              </td>
              <td style={{ width: 90 }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="border rounded" style={{ width: 22, height: 22, background: item.bgColor }} />
                  <code className="small">{item.bgColor}</code>
                </div>
              </td>
              <td style={{ width: 120 }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold">{item.order}</span>
                  <div className="d-flex flex-column gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => swapOrder(idx, idx - 1)}
                      disabled={idx === 0}
                      style={{ padding: '2px 8px' }}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => swapOrder(idx, idx + 1)}
                      disabled={idx === sortedItems.length - 1}
                      style={{ padding: '2px 8px' }}
                    >
                      ↓
                    </Button>
                  </div>
                </div>
              </td>
              <td style={{ width: 110 }}>
                <Badge bg={item.isActive ? 'success' : 'secondary'}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className="text-truncate" style={{ maxWidth: 260 }}>
                <div className="fw-semibold">{item.ctaText}</div>
                <small className="text-muted">{item.ctaLink}</small>
              </td>
              <td className="text-nowrap">
                <Button size="sm" variant="outline-primary" onClick={() => onEdit(item)}>
                  Edit
                </Button>{' '}
                <Button size="sm" variant="outline-secondary" onClick={() => toggleActive(item)}>
                  Toggle
                </Button>{' '}
                <Button size="sm" variant="outline-danger" onClick={() => remove(item.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {sortedItems.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No slides yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  );
}
